import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Button,
  Space,
  Alert,
  List,
  Typography,
  Divider,
  Card,
  Row,
  Col,
  Statistic,
  message,
  Select,
  Tag
} from 'antd';
import {
  ScissorOutlined,
  FileTextOutlined,
  NumberOutlined,
  CheckCircleOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import apiClient from '../utils/apiClient';

const { TextArea } = Input;
const { Title, Text } = Typography;
const { Option } = Select;

const ScriptSplitter = ({ visible, onClose, onSplitAndCreate }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [splitResult, setSplitResult] = useState(null);
  const [selectedVoice, setSelectedVoice] = useState('af_bella');
  const [voices, setVoices] = useState([]);
  const [playingDemo, setPlayingDemo] = useState(null);
  const [playingChunk, setPlayingChunk] = useState(null); // Track which chunk is playing
  const chunkAudioRef = React.useRef(null); // Reference to current chunk audio

  // Load voices when component mounts
  useEffect(() => {
    const fetchVoices = async () => {
      try {
        const response = await apiClient.get('/api/voices/');
        const voicesData = response.data?.result?.voices || [];

        // Transform API response to our format
        const transformedVoices = voicesData.map(voice => ({
          code: voice.code,
          name: voice.name,
          gender: voice.gender,
          language: voice.language || 'en-US',
          description: voice.description,
          demo: voice.demo  // Use demo URL from API
        }));
        setVoices(transformedVoices);
      } catch (error) {
        console.error('❌ fetchVoices error:', error);
        // Fallback to UnrealSpeech voices if API fails (API v8 format)
        const fallbackVoices = [
          // English (US) - Female
          { code: 'af_bella', name: 'Bella (en-US)', gender: 'female', language: 'en-US', description: 'American female voice' },
          { code: 'af_sarah', name: 'Sarah (en-US)', gender: 'female', language: 'en-US', description: 'American female voice' },
          { code: 'af_nicole', name: 'Nicole (en-US)', gender: 'female', language: 'en-US', description: 'American female voice' },
          { code: 'af_sky', name: 'Sky (en-US)', gender: 'female', language: 'en-US', description: 'American female voice' },

          // English (US) - Male
          { code: 'am_adam', name: 'Adam (en-US)', gender: 'male', language: 'en-US', description: 'American male voice' },
          { code: 'am_michael', name: 'Michael (en-US)', gender: 'male', language: 'en-US', description: 'American male voice' },

          // English (UK) - Female
          { code: 'bf_emma', name: 'Emma (en-UK)', gender: 'female', language: 'en-UK', description: 'British female voice' },
          { code: 'bf_isabella', name: 'Isabella (en-UK)', gender: 'female', language: 'en-UK', description: 'British female voice' },

          // English (UK) - Male
          { code: 'bm_george', name: 'George (en-UK)', gender: 'male', language: 'en-UK', description: 'British male voice' },
          { code: 'bm_lewis', name: 'Lewis (en-UK)', gender: 'male', language: 'en-UK', description: 'British male voice' },

          // French - Female
          { code: 'ff_siwis', name: 'Siwis (fr-FR)', gender: 'female', language: 'fr-FR', description: 'French female voice' },

          // Hindi - Female
          { code: 'hf_alpha', name: 'Alpha (hi-IN)', gender: 'female', language: 'hi-IN', description: 'Hindi female voice' },
          { code: 'hf_beta', name: 'Beta (hi-IN)', gender: 'female', language: 'hi-IN', description: 'Hindi female voice' },

          // Hindi - Male
          { code: 'hm_omega', name: 'Omega (hi-IN)', gender: 'male', language: 'hi-IN', description: 'Hindi male voice' },

          // Japanese - Female
          { code: 'jf_alpha', name: 'Alpha (ja-JP)', gender: 'female', language: 'ja-JP', description: 'Japanese female voice' },
          { code: 'jf_gongitsune', name: 'Gongitsune (ja-JP)', gender: 'female', language: 'ja-JP', description: 'Japanese female voice' },

          // Japanese - Male
          { code: 'jm_kumo', name: 'Kumo (ja-JP)', gender: 'male', language: 'ja-JP', description: 'Japanese male voice' },

          // Spanish - Female
          { code: 'ef_dora', name: 'Dora (es-ES)', gender: 'female', language: 'es-ES', description: 'Spanish female voice' },

          // Spanish - Male
          { code: 'em_alex', name: 'Alex (es-ES)', gender: 'male', language: 'es-ES', description: 'Spanish male voice' },

          // Italian - Female
          { code: 'if_sara', name: 'Sara (it-IT)', gender: 'female', language: 'it-IT', description: 'Italian female voice' },

          // Italian - Male
          { code: 'im_nicola', name: 'Nicola (it-IT)', gender: 'male', language: 'it-IT', description: 'Italian male voice' },

          // Chinese - Female
          { code: 'zf_xiaobei', name: 'Xiaobei (zh-CN)', gender: 'female', language: 'zh-CN', description: 'Chinese female voice' },
          { code: 'zf_xiaoni', name: 'Xiaoni (zh-CN)', gender: 'female', language: 'zh-CN', description: 'Chinese female voice' },

          // Chinese - Male
          { code: 'zm_yunjian', name: 'Yunjian (zh-CN)', gender: 'male', language: 'zh-CN', description: 'Chinese male voice' },

          // Portuguese - Female
          { code: 'pf_dora', name: 'Dora (pt-BR)', gender: 'female', language: 'pt-BR', description: 'Portuguese female voice' },

          // Portuguese - Male
          { code: 'pm_alex', name: 'Alex (pt-BR)', gender: 'male', language: 'pt-BR', description: 'Portuguese male voice' },
        ];
        setVoices(fallbackVoices);
      }
    };

    if (visible) {
      fetchVoices();
    } else {
      // Cleanup audio when modal closes
      if (chunkAudioRef.current) {
        chunkAudioRef.current.pause();
        chunkAudioRef.current.src = '';
        chunkAudioRef.current = null;
      }
      setPlayingChunk(null);
      setPlayingDemo(null);
    }
  }, [visible]);

  const handleVoiceDemo = async (voice) => {
    try {
      setLoading(true);

      // Single source: serve demo directly from server voices folder
      const demoUrl = `/api/voices/file/${voice.code}`;
      console.log('[Demo] requesting', demoUrl);

      const response = await apiClient.get(demoUrl, { responseType: 'blob' });
      console.log('[Demo] response', { status: response.status, type: response.headers['content-type'] });

      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'audio/mpeg' });
      const url = URL.createObjectURL(blob);

      // Stop currently playing demo (if any)
      if (playingDemo && chunkAudioRef.current) {
        try {
          chunkAudioRef.current.pause();
          chunkAudioRef.current.currentTime = 0;
        } catch (e) {
          console.warn('[Demo] stop previous error', e);
        }
      }

      const audio = new Audio(url);

      audio.addEventListener('error', (e) => {
        console.error('[Demo] audio playback error', e);
      });

      audio.addEventListener('ended', () => {
        setPlayingDemo(null);
      });

      chunkAudioRef.current = audio;
      setPlayingDemo(voice.code);

      await audio.play();
      message.success(`🎵 Playing demo for ${voice.name}`);
    } catch (error) {
      console.error('❌ Voice demo error:', error);
      const status = error?.response?.status;
      if (status === 404) {
        message.error(`Demo file not found on server for ${voice.name}`);
      } else {
        message.error(`Failed to play demo for ${voice.name}: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePlayChunkDemo = async (chunkText, index) => {
    // Stop any currently playing demo or chunk
    if (playingDemo) setPlayingDemo(null);
    if (chunkAudioRef.current) {
      chunkAudioRef.current.pause();
      chunkAudioRef.current = null;
    }

    // If the same chunk is clicked again, stop it
    if (playingChunk === index) {
      setPlayingChunk(null);
      return;
    }

    setPlayingChunk(index);
    try {
      message.loading({ content: 'Generating audio for chunk...', key: `chunk-audio-${index}` });
      const response = await apiClient.post('/api/tts/', {
        text: chunkText,
        voice_code: selectedVoice,
      }, {
        responseType: 'blob' // Important for receiving audio blob
      });

      const audioBlob = new Blob([response.data], { type: 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);

      chunkAudioRef.current = audio; // Store reference to current audio

      audio.addEventListener('canplaythrough', () => {
        message.success({ content: `🎵 Playing chunk ${index + 1} demo`, key: `chunk-audio-${index}` });
      });

      audio.addEventListener('ended', () => {
        setPlayingChunk(null);
        URL.revokeObjectURL(audioUrl); // Clean up the object URL
      });

      audio.addEventListener('error', (e) => {
        console.error('❌ Chunk audio playback error:', e);
        message.error({ content: `Failed to play chunk ${index + 1} demo.`, key: `chunk-audio-${index}` });
        setPlayingChunk(null);
        URL.revokeObjectURL(audioUrl);
      });

      await audio.play();
    } catch (error) {
      console.error('❌ Error generating chunk audio:', error);
      message.error({ content: error.response?.data?.detail || error.message || `Failed to generate audio for chunk ${index + 1}.`, key: `chunk-audio-${index}` });
      setPlayingChunk(null);
    }
  };

  const handleExportToTxt = () => {
    if (!splitResult || !splitResult.chunks) {
      message.warning('No split results to export');
      return;
    }

    try {
      // Format chunks with separators
      const content = splitResult.chunks
        .map((chunk, idx) => `Chunk ${idx + 1}: (${chunk.length} characters)\n${chunk}`)
        .join('\n\n---\n\n');

      // Add header
      const header = `Script Split Results\n` +
        `Total Chunks: ${splitResult.total_chunks}\n` +
        `Total Characters: ${splitResult.total_chars}\n` +
        `Max Characters per Chunk: ${splitResult.max_chars}\n` +
        `Selected Voice: ${voices.find(v => v.code === selectedVoice)?.name || selectedVoice}\n` +
        `\n${'='.repeat(80)}\n\n`;

      const fullContent = header + content;

      // Create and download file
      const blob = new Blob([fullContent], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `split_chunks_${Date.now()}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      message.success(`✅ Exported ${splitResult.total_chunks} chunks to TXT file`);
    } catch (error) {
      console.error('❌ Export error:', error);
      message.error('Failed to export chunks');
    }
  };

  const handleSplit = async (values) => {
    setLoading(true);
    try {
      const response = await apiClient.post('/api/script-splitter/split-script', {
        script: values.script,
        max_chars: values.max_chars || 1000
      });

      setSplitResult(response.data);
      message.success(`Script split into ${response.data.total_chunks} chunks`);
    } catch (error) {
      console.error('Error splitting script:', error);
      message.error(error.response?.data?.detail || error.message || 'Failed to split script');
    } finally {
      setLoading(false);
    }
  };

  const handleSplitAndCreate = async () => {
    if (!splitResult) return;

    setLoading(true);
    try {
      const response = await apiClient.post('/api/script-splitter/split-and-create-requests', {
        script: form.getFieldValue('script'),
        max_chars: form.getFieldValue('max_chars') || 1000,
        voice_code: selectedVoice,
        project: form.getFieldValue('project')
      });

      message.success(response.data.message);

      // Call parent callback to refresh data
      if (onSplitAndCreate) {
        onSplitAndCreate();
      }

      // Reset form and close modal
      form.resetFields();
      setSplitResult(null);
      onClose();
    } catch (error) {
      console.error('Error creating requests:', error);
      message.error(error.response?.data?.detail || error.message || 'Failed to create requests');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setSplitResult(null);
    onClose();
  };

  return (
    <Modal
      title={
        <Space>
          <ScissorOutlined />
          <span>Script Splitter</span>
        </Space>
      }
      open={visible}
      onCancel={handleClose}
      width={800}
      footer={null}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSplit}
        initialValues={{
          max_chars: 1000
        }}
      >
        <Alert
          message="Script Splitter"
          description="Paste your long script here. The tool will automatically split it into smaller chunks based on punctuation marks (., !, ?, ;, :) with your specified character limit."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form.Item
          name="project"
          label="Project (Optional)"
        >
          <Input
            placeholder="Enter project name to group related requests"
            maxLength={100}
          />
        </Form.Item>

        <Form.Item
          name="script"
          label="Script Text"
          rules={[
            { required: true, message: 'Please input script text!' },
            { min: 50, message: 'Script must be at least 50 characters!' }
          ]}
        >
          <TextArea
            rows={8}
            placeholder="Paste your long script here... The tool will automatically split it into smaller chunks based on punctuation marks."
            showCount
            maxLength={500000}
          />
        </Form.Item>

        <Form.Item
          name="max_chars"
          label="Max Characters per Chunk"
          rules={[
            { required: true, message: 'Please input max characters!' },
            {
              validator: (_, value) => {
                if (value && (isNaN(Number(value)) || Number(value) < 100 || Number(value) > 1000)) {
                  return Promise.reject(new Error('Max characters must be between 100 and 1000!'));
                }
                return Promise.resolve();
              }
            }
          ]}
        >
          <Input
            type="number"
            min="100"
            max="1000"
            placeholder="1000"
            suffix="characters"
          />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              icon={<ScissorOutlined />}
            >
              Split Script
            </Button>
            <Button onClick={handleClose}>
              Cancel
            </Button>
          </Space>
        </Form.Item>
      </Form>

      {splitResult && (
        <>
          <Divider />

          <Card>
            <Row gutter={16}>
              <Col span={8}>
                <Statistic
                  title="Total Chunks"
                  value={splitResult.total_chunks}
                  prefix={<NumberOutlined />}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Total Characters"
                  value={splitResult.total_chars}
                  prefix={<FileTextOutlined />}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Avg per Chunk"
                  value={Math.round(splitResult.total_chars / splitResult.total_chunks)}
                  suffix="chars"
                />
              </Col>
            </Row>
          </Card>

          <Divider />

          <Title level={4}>
            <CheckCircleOutlined /> Split Results
          </Title>

          <List
            dataSource={splitResult.chunks}
            renderItem={(chunk, index) => (
              <List.Item>
                <Card size="small" style={{ width: '100%' }}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Space>
                      <Text strong>Chunk {index + 1}:</Text>
                      <Text type="secondary">({chunk.length} characters)</Text>
                    </Space>
                    <Text style={{ whiteSpace: 'pre-wrap' }}>{chunk}</Text>
                  </Space>
                </Card>
              </List.Item>
            )}
            pagination={{
              pageSize: 5,
              simple: true,
              position: 'bottom',
              align: 'center'
            }}
          />

          <Divider style={{ marginTop: 14 }} />

          <Form layout="inline" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 14 }}>
            <Form.Item label="Voice for all chunks:">
              <Select
                value={selectedVoice}
                onChange={setSelectedVoice}
                placeholder="Select voice"
                style={{ width: 350 }}
                showSearch
                filterOption={(input, option) => {
                  const voice = voices.find(v => v.code === option.value);
                  const voiceName = voice?.name || voice?.code || '';
                  return voiceName.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                }}
                optionLabelProp="label"
              >
                {voices.map(voice => (
                  <Option
                    key={voice.code}
                    value={voice.code}
                    label={`${voice.name || voice.code} (${voice.gender})`}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontWeight: 'bold' }}>{voice.name || voice.code}</span>
                        <Tag color={voice.gender === 'female' ? 'pink' : 'blue'} size="small">
                          {voice.gender}
                        </Tag>
                      </div>
                      <Button
                        type="primary"
                        size="middle"
                        icon={<PlayCircleOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVoiceDemo(voice);
                        }}
                        style={{
                          height: '20px',
                          padding: '0 6px',
                          fontSize: '10px',
                          minWidth: '50px'
                        }}
                        title="Play voice demo"
                      >
                        Demo
                      </Button>
                    </div>
                    {voice.description && (
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                        {voice.description}
                      </div>
                    )}
                    {voice.language && (
                      <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>
                        Language: {voice.language}
                      </div>
                    )}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item>
              <Button
                type="default"
                onClick={handleExportToTxt}
                icon={<DownloadOutlined />}
                style={{ marginRight: 8 }}
              >
                Export to TXT
              </Button>
              <Button
                type="primary"
                onClick={handleSplitAndCreate}
                loading={loading}
                icon={<CheckCircleOutlined />}
              >
                Create {splitResult.total_chunks} Text Requests
              </Button>
            </Form.Item>
          </Form>
        </>
      )}
    </Modal>
  );
};

export default ScriptSplitter;